type EffectiveConnectionType = 'slow-2g' | '2g' | '3g' | '4g'

type NetworkConnection = {
  effectiveType?: EffectiveConnectionType | string
  addEventListener?: (type: 'change', listener: () => void) => void
  removeEventListener?: (type: 'change', listener: () => void) => void
}

function getNetworkConnection(): NetworkConnection | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkConnection
    mozConnection?: NetworkConnection
    webkitConnection?: NetworkConnection
  }
  return nav.connection || nav.mozConnection || nav.webkitConnection
}

export function getNetworkEffectiveType(): string | undefined {
  return getNetworkConnection()?.effectiveType
}

/**
 * Raises the preferred poll interval on slower effective connection types.
 */
export function getAdaptivePollBaseIntervalMs(preferredIntervalMs: number): number {
  const effectiveType = getNetworkEffectiveType()
  if (effectiveType === 'slow-2g' || effectiveType === '2g') {
    return Math.max(preferredIntervalMs, 60_000)
  }
  if (effectiveType === '3g') {
    return Math.max(preferredIntervalMs, 30_000)
  }
  return preferredIntervalMs
}

/**
 * Stretches Axios timeouts on slower effective connection types.
 */
export function getAdaptiveHttpTimeoutMs(baseTimeoutMs: number): number {
  const effectiveType = getNetworkEffectiveType()
  if (effectiveType === 'slow-2g' || effectiveType === '2g') {
    return Math.round(baseTimeoutMs * 2)
  }
  if (effectiveType === '3g') {
    return Math.round(baseTimeoutMs * 1.5)
  }
  return baseTimeoutMs
}

export function subscribeToNetworkChanges(listener: () => void): () => void {
  const connection = getNetworkConnection()
  if (!connection?.addEventListener || !connection.removeEventListener) {
    return () => {}
  }
  connection.addEventListener('change', listener)
  return () => {
    connection.removeEventListener?.('change', listener)
  }
}
